import {
  isRegisteredTenantCreateOperation,
  type RegisteredTenant,
} from '@festina/configuration-general-types/configuration'
import {
  type BranchKey,
  resolveTopologicalPathForBlock,
} from '@festina-life/api'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import { useFetchBranchChangesets } from '../api/changesets/useFetchBranchChangesets'

type FetchConfigurationTenantProps = {
  enabled: boolean
  branchKey: Optional<BranchKey>
  block: Optional<string>
}

export const useFetchConfigurationTenants = ({
  enabled,
  branchKey,
  block,
}: FetchConfigurationTenantProps) => {
  const { data: fetchedBranchChangesets, isLoading } = useFetchBranchChangesets(
    {
      enabled: enabled && !!block,
      branchKey,
      includeBaseChangesets: true,
      shouldIncludeExtrinsicParameterValues: false,
    },
  )
  const { blockTopology } = useMetaModelContext()
  const translate = useTranslate()
  const visibleChangesetsOnBlock = useMemo(() => {
    const blockPath = block
      ? resolveTopologicalPathForBlock(block, blockTopology)
      : []

    return fetchedBranchChangesets?.branchChangesets
      ?.filter(notEmpty)
      .filter((changeset) => {
        assertNotEmpty(changeset.block, translate('error.noBlocks'))
        return blockPath.includes(changeset.block)
      })
  }, [
    block,
    blockTopology,
    fetchedBranchChangesets?.branchChangesets,
    translate,
  ])

  return useMemo(() => {
    if (!enabled) {
      return { data: null, isLoading }
    }

    const indexedTenantsById = new Map<string, RegisteredTenant>()

    visibleChangesetsOnBlock
      ?.filter(notEmpty)
      .flatMap(({ changeset }) =>
        (changeset?.operations ?? [])
          .filter(notEmpty)
          .filter(isRegisteredTenantCreateOperation)
          .map(
            (registeredTenantOperation) =>
              registeredTenantOperation.registeredTenant,
          )
          .filter(notEmpty),
      )
      .forEach((registeredTenant) => {
        assertNotEmpty(registeredTenant.tenantId)
        indexedTenantsById.set(registeredTenant.tenantId, registeredTenant)
      })

    return { data: Array.from(indexedTenantsById.values()), isLoading }
  }, [enabled, visibleChangesetsOnBlock, isLoading])
}
